import filterCloudOrUriFilesAndShowError from './filterCloudOrUriFilesAndShowError';
import { MAIL_ITEM_CONTENT_TYPE } from '../constants';
import createSmimeAttachmentViaSmimeQueueManager from '../SmimeAttachmentQueueManager/createSmimeAttachmentViaSmimeQueueManager';
import { setIsPlaceholderAttachment } from 'owa-attachment-data';
import { AttachmentFile, AttachmentFileList, AttachmentFileType } from 'owa-attachment-file-types';
import { AttachmentItemFile } from 'owa-attachment-file-types';
import setOngoingAction from 'owa-attachment-full-data/lib/actions/setOngoingAction';
import { SmimeAttachmentType } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import filterFilesToAttach from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/filterFilesToAttach';
import ifIsOverSizeLimitShowErrorMessage from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/ifIsOverSizeLimitShowErrorMessage';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import convertFileToBase64String from 'owa-encoding-utils/lib/utils/base64/convertFileToBase64String';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import ItemAttachment from 'owa-service/lib/contract/ItemAttachment';
import attachmentId from 'owa-service/lib/factory/attachmentId';
import fileAttachment from 'owa-service/lib/factory/fileAttachment';
import item from 'owa-service/lib/factory/item';
import itemAttachment from 'owa-service/lib/factory/itemAttachment';
import itemIdAttachment from 'owa-service/lib/factory/itemIdAttachment';
import { createContentId, getIdWithSmimePrefix } from 'owa-smime-adapter/lib/utils/smimeUtils';
import {
    createInlineAttachmentState,
    setInlineAttachmentStatus,
    InlineAttachmentStatus,
    ActionType,
    isInlineAttachmentStateType,
    AttachmentState,
    createAttachmentFullViewState,
    initializeAttachmentFullViewState,
    AttachmentFullViewState,
} from 'owa-attachment-full-data';
import {
    AttachmentWellViewState,
    CreateAttachmentsProperties,
    lazyAddAttachmentIntoWellBeforeUpload,
} from 'owa-attachment-well-data';

export default async function createSmimeAttachmentsFromFiles(
    files: AttachmentFileList,
    parentItemId: ClientItemId,
    attachmentWellViewState: AttachmentWellViewState,
    properties: CreateAttachmentsProperties,
    infoBar: InfoBarHostViewState
): Promise<AttachmentFileList> {
    const { isInline } = properties;

    // Run the filters to remove unsupported files.
    files = filterFilesToAttach(files, isInline, infoBar);
    files = filterCloudOrUriFilesAndShowError(files, infoBar);
    if (ifIsOverSizeLimitShowErrorMessage(files, infoBar, attachmentWellViewState)) {
        return files;
    }

    //Process the attachments
    const addAttachmentIntoWellBeforeUpload = await lazyAddAttachmentIntoWellBeforeUpload.import();
    for (let i = 0, file: AttachmentFile; (file = files[i]); i++) {
        const smimeAttachmentId: ClientAttachmentId = {
            ...attachmentId({ Id: getIdWithSmimePrefix() }),
            mailboxInfo: parentItemId.mailboxInfo,
        };
        let smimeAttachment: SmimeAttachmentType;
        if (file.fileType === AttachmentFileType.MailItem) {
            smimeAttachment = itemAttachment({
                Name: file.name,
                Item: item({
                    ItemId: {
                        Id: file.itemId,
                    },
                }),
                Size: file.size,
                ContentType: MAIL_ITEM_CONTENT_TYPE,
                AttachmentId: smimeAttachmentId,
            });
        } else if (file.fileType === AttachmentFileType.AttachmentItem) {
            const attachmentItemFile = file as AttachmentItemFile;
            // Mail attachment items do not contain ContentType. Improper ContentType can cause corruption of few types of files. eg: Excel files
            smimeAttachment = itemIdAttachment({
                Name: file.name,
                Size: file.size,
                ContentType: attachmentItemFile.ContentType || file.type,
                AttachmentIdToAttach: attachmentItemFile.attachmentItemId,
                AttachmentId: smimeAttachmentId,
            });
            // EmbeddedItemClass should exist if attachmentItem happens to be a mail
            (<ItemAttachment>smimeAttachment).EmbeddedItemClass =
                attachmentItemFile.EmbeddedItemClass;
        } else {
            // Handles local file and image attachments
            smimeAttachment = fileAttachment({
                Name: file.name,
                Size: file.size,
                ContentType: file.type,
                IsInline: isInline,
                // Content will be set in updateSmimeAttachment method call
                Content: '',
                ContentId: null,
                ContentLocation: null,
                AttachmentOriginalUrl: null,
                LastModifiedTime: null,
                IsInlineToUniqueBody: false,
                IsInlineToNormalBody: false,
                ThumbnailMimeType: null,
                Thumbnail: null,
                IsContactPhoto: false,
                ImageThumbnailSalientRegions: null,
                ImageThumbnailHeight: 0,
                ImageThumbnailWidth: 0,
                AttachmentId: smimeAttachmentId,
            });
            if (isInline) {
                smimeAttachment.ContentId = createContentId();
            }
        }

        const attachmentState = createAttachmentState(
            properties,
            smimeAttachmentId,
            true /* attachCompleted */,
            AttachmentFileType.Smime
        );
        /**
         * addAttachmentIntoWellBeforeUpload adds the attachment state and attachment type to the well and store.
         * There is nothing specific to the upload in this action, hence using it for S/MIME scenario.
         *  */
        addAttachmentIntoWellBeforeUpload(
            attachmentState,
            attachmentWellViewState,
            smimeAttachment,
            smimeAttachmentId
        );

        if (
            file.fileType === AttachmentFileType.MailItem ||
            file.fileType === AttachmentFileType.AttachmentItem ||
            file.fileType === AttachmentFileType.AttachmentItemSuggestion
        ) {
            createSmimeAttachmentViaSmimeQueueManager(
                attachmentWellViewState,
                parentItemId,
                infoBar,
                [attachmentState]
            );
        } else {
            // Update attachment state and set Content property
            updateSmimeAttachment(file, attachmentState, smimeAttachment);
        }
    }

    return files;
}

function createAttachmentState(
    properties: CreateAttachmentsProperties,
    attachmentId: ClientAttachmentId,
    attachCompleted: boolean,
    fileType: AttachmentFileType
): AttachmentState {
    const { isInline, isHiddenInline } = properties;
    return isInline
        ? createInlineAttachmentState(attachmentId, attachmentId.Id, isHiddenInline)
        : createAttachmentFullViewState(
              attachmentId,
              false /* isReadOnly */,
              attachCompleted,
              false /* shouldShare */,
              fileType,
              true /* isPlaceholderAttachment */
          );
}

async function updateSmimeAttachment(
    file: AttachmentFile,
    attachmentState: AttachmentState,
    smimeAttachment: SmimeAttachmentType
) {
    if (file.fileType === AttachmentFileType.Local && file.fileObject) {
        smimeAttachment.Content = await convertFileToBase64String(file.fileObject);
    } else if (file.fileType === AttachmentFileType.Base64InlineImage) {
        smimeAttachment.Content = file.dataUri;
    }
    if (isInlineAttachmentStateType(attachmentState)) {
        setInlineAttachmentStatus(
            attachmentState,
            InlineAttachmentStatus.CreateAttachmentSucceeded
        );
    } else {
        setIsPlaceholderAttachment(attachmentState, false);
        setOngoingAction(attachmentState, ActionType.None);
        const attachmentFullViewState = attachmentState as AttachmentFullViewState;
        initializeAttachmentFullViewState(
            attachmentFullViewState,
            getAttachment(smimeAttachment.AttachmentId),
            false /* treatLinksAsAttachments */,
            attachmentFullViewState.strategy.supportedMenuActions
        );
    }
}
